import * as yup from "yup";

import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	SingleColumnLayout,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import { Box, Button, Card, Grid, Typography, useTheme } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import {
	IGetListRequest,
	IResponse,
	IServerError,
} from "../../../../models/common";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	scrollPanelFieldsToTop,
	sortDDLByLabel,
	useNavigateToLogout,
} from "../../../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BasicSetupFormToolbar } from "../../../BasicSetupFormToolbar";
import { CountryService } from "../../../../services/common/CountryService";
import { DDLDataTable } from "../../../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../../../EnhancedButtonPrimeReact";
import { IAppState } from "../../../../store/reducers";
import { ITrustBank } from "../../../../models/DirectDeposit/TrustBank";
import { TrustBankService } from "../../../../services/DirectDeposit/TrustBankService";
import { TrustBankSetupFieldsPanel } from "./Panels/TrustBankSetupFieldsPanel";
import { TrustBankSetupFieldsRightPanel } from "./Panels/TrustBankSetupFieldsRightPanel";
import { TrustBankSetupToolbar } from "./TrustBankSetupToolbar/TrustBankSetupToolbar";
import { TrustBankSetupValidationSchema } from "./validator";
import { queryClient } from "../../../..";
import { setSelectedDrawerOptionsMenu } from "../../../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../../../store/actions/stickyNavBarActions";
import { yupResolver } from "@hookform/resolvers/yup";

// import { ITrustBank } from "../../../../models";











// import { IClient } from "../../models/client";



























export interface ITrustBankSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "trust-single-data-record";
const TRUST_BANKS_DDL_DATA_QUERY_KEY = "trust-bank-ddl";

const useGetDataTrustBankByIdById = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id],
		queryFn: () => {
			return TrustBankService.getTrustBankDataById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetTrustDDL = () =>
	useQuery({
		queryKey: [TRUST_BANKS_DDL_DATA_QUERY_KEY],
		queryFn: () => {
			return TrustBankService.getTrustDDL();
		},
		refetchOnWindowFocus: false,
	});

export const TrustBankSetup: React.FC<ITrustBankSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const [openDialog, setDialogOpen] = useState(false);
	const [isPendingData, setIsPendingData] = useState(false);
	const accountantId = useSelector((state: IAppState) => state.accountant.id);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [trustBanksId, setTrustBanksId] = useState<number | string>(0);
	const [trustBankId, setTrustBankId] = useState<number>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);

	const queryTrust = useQueryClient();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const navigateToLogout = useNavigateToLogout();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "departmentName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null ? requestParams.sortField : "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	useEffect(() => {
		setEditRecordId(id)
	}, [id])

	const { isPending: isSubmitting, mutate: submitTrustBankFormFields } =
		useMutation({
			mutationFn: TrustBankService.createTrustBank,
			onSuccess: (res: ITrustBank | IServerError | IResponse | any) => {

				if (res && res.status === 200) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					scrollPanelFieldsToTop();
					let path = `/direct-deposit/trust-bank/setup/${res.data.id}`;
					navigate(path)

					return Promise.all([
						queryTrust.invalidateQueries({
							queryKey: [TRUST_BANKS_DDL_DATA_QUERY_KEY],
						}),
						queryTrust.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);

				} else if (res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					scrollPanelFieldsToTop();
					let path = `/direct-deposit/trust-bank/setup/${res.data.id}`;
					navigate(path);

					setEditRecordId(res.data.id);
					return Promise.all([
						queryTrust.invalidateQueries({
							queryKey: [TRUST_BANKS_DDL_DATA_QUERY_KEY],
						}),
						queryTrust.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);


				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {

				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const formMethods = useForm<any>({
		resolver: yupResolver(TrustBankSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const { data: selectedDataRecord } = useGetDataTrustBankByIdById(
		editRecordId,
		navigateToLogout
	);

	const { isPending, data: trustDDL } = useGetTrustDDL();



	const upadtedTrustBankListItems = useMemo(() => {
		return (
			trustDDL &&
			trustDDL &&
			sortDDLByLabel(trustDDL).map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/direct-deposit/trust-bank/setup/${item.value}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.value)}>
						{item.label}
					</Link>
				),
			}))
		);
	}, [trustDDL]);


	const onSubmit = (values: any) => {

		if (id) {
			let payload = {
				id: id && id,
				// loadTrustBank: 100, // remove when deployed
				bank: values.bank,
				transactionMethod: values.transactionMethod,
				trustBankName: values.trustBankName,
				debitTransactionCode: values.debitTransactionCode,
				debitClientId: values.debitClientId,
				creditTransactionCode: values.creditTransactionCode,
				creditClientId: values.creditClientId,
				defaultTrustBank: values.defaultTrustBank,
				clientName: values.clientName,
				clientShortName: values.clientShortName,
				bankAccountNo: values.bankAccountNo,
				financialInstitutionNo: values.financialInstitutionNo,
				bankTransitNo: values.bankTransitNo,
				accountTitle: values.accountTitle,
				emailForUpload: values.emailForUpload,
			};
			let finalPayload = {
				payload: payload,
				navigateToLogout: navigateToLogout,
			}
			submitTrustBankFormFields(finalPayload);

		} else {
			{
				setSnackbarData({
					open: true,
					message: "Please Select Trust.",
					severity: "warning",
				});
			}
		}
	};

	useEffect(() => {
		if (id && selectedDataRecord) {
			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
			});
		}
	}, [id && selectedDataRecord]);


	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteTrustById } = useMutation({
		mutationFn: TrustBankService.delTrust,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {

				queryTrust.invalidateQueries({
					queryKey: [TRUST_BANKS_DDL_DATA_QUERY_KEY],
				});
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			scrollPanelFieldsToTop();
			formMethods.reset();
			let path = `/direct-deposit/trust-bank/setup`;
			navigate(path);
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteTrustField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				trustBankId: trustBankId,
				navigateToLogout: navigateToLogout,
			}
			deleteTrustById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setTrustBankId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setTrustBankId(params.value);
	}, []);

	const { isPending: isArchive, mutate: archiveTrustBanksById } = useMutation({
		mutationFn: TrustBankService.archiveTrustBank,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the ClientBanks fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/direct-deposit/trust-bank/setup`;
			navigate(path);
			return queryTrust.invalidateQueries({
				queryKey: [TRUST_BANKS_DDL_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveTrustBanksField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				trustBanksId: trustBanksId,
				navigateToLogout: navigateToLogout,
			};
			archiveTrustBanksById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setTrustBanksId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onRowEditClicked = useCallback((params: any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/direct-deposit/trust-bank/setup/${params.value}`;
		navigate(path);
	}, []);

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		scrollPanelFieldsToTop();
		let path = `/direct-deposit/trust-bank/setup`;
		navigate(path);
	};

	const expandedLabels = ["Transactions"];
	const expandedHomeBankSetupOrSetupItem = "Trust Bank"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === "Transactions" && item.label === expandedHomeBankSetupOrSetupItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedHomeBankSetupOrSetupItem && "/direct-deposit/trust-bank/setup",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {

		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid

		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingData || isDeleting || isArchive || isPending}
				testId="trust bank Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteTrustField}
				cancelActionHandler={onCloseHandler}
				testId="trust-bank-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}

					>
						<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}
						>
							<TwoColumnLayout

								toolbar={
									<BasicSetupFormToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
										pageTitle="Trust Bank"
									/>
								}
								leftColumnContent={
									<>
										<StandardPanel
											title="Account 1"
											testId={`account1-bank-setup`}
											showTitle={true}
											// variant="outlined"
											topPadding={2}
											borderHide={true}

										>
											<TrustBankSetupFieldsPanel
												testId={"Trust-Bank-info-panel"}
											/>
										</StandardPanel>

									</>
								}
								rightColumnContent={
									<>
										<StandardPanel
											title="Account 2"
											testId={`account2-bank-setup`}
											showTitle={true}
											// variant="outlined"
											topPadding={2}
											borderHide={true}
										>
											<TrustBankSetupFieldsRightPanel
												testId={"Trust-Bank-info-right-panel"}
											/>
										</StandardPanel>
									</>
								}
								toolbarPosition="top"
								testId="form-layout"

							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 1, lg: 1, xl: 1 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									trustDDL && upadtedTrustBankListItems ? upadtedTrustBankListItems : []}
								columns={[
									{ field: "label", headerName: "Label" },
									{ field: "defaultTrustBank", headerName: "Default" },
								]}
								showHeaderColumns={true}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								// isLoading={isPending}
								// totalRecords={
								// 	trustBankList && trustBankList.total
								// 		? trustBankList.total
								// 		: 0
								// }
								totalRecords={

									trustDDL && trustDDL
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								showPaginator={false}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveTrustBanksField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"trust-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
